import { useSelector } from 'react-redux';

import { selectors as authSelectors } from 'src/features/auth';
import { getFeatureFlagIsLoading } from 'src/features/featureFlag/featureFlagSlice';
import { selectors as propertySelectors } from 'src/features/property';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag/useFeatureFlag';
import { getActiveRouteOptions } from 'src/router/navOptions';
import { FeatureFlagKey, RoleEnum } from 'src/ts/enums';
import { INavOption } from 'src/ts/interfaces/navOption';

interface UseNavOptionPermissionsResult {
  permissions: RoleEnum[] | undefined;
  isLoading: boolean;
}

const useNavOptionPermissionsWithLoading = (nameOption: string): UseNavOptionPermissionsResult => {
  const hasIdentityVerificationPropertyEnabled =
    useSelector(propertySelectors.hasIdentityVerificationPropertyEnabled.data) ?? false;

  const authUser = useSelector(authSelectors.user);
  const isAnalyticsAndReportingEnabled = useFeatureFlag(FeatureFlagKey.ANALYTICS_AND_REPORTING_NAV);
  const isFeatureFlagLoading = useSelector(getFeatureFlagIsLoading);

  const isDocumentLinksTabEnabled = !authUser?.properties?.every(
    (property) => property.disable_dup_fd
  );

  // If feature flags are still loading, return loading state
  if (isFeatureFlagLoading) {
    return {
      permissions: undefined,
      isLoading: true
    };
  }

  const navOptions = getActiveRouteOptions(
    hasIdentityVerificationPropertyEnabled,
    isDocumentLinksTabEnabled,
    isAnalyticsAndReportingEnabled
  );
  const option = navOptions.find((option) => option?.name === nameOption);

  // If the option is not found after feature flags are loaded, it means the feature is disabled
  if (!option) {
    return {
      permissions: [],
      isLoading: false
    };
  }

  return {
    permissions: (option as INavOption)?.permissions,
    isLoading: false
  };
};

export default useNavOptionPermissionsWithLoading;
